import { apiClient, type Paginated } from '@/core/api';
import type {
  CourseDto,
  CourseListParams,
  CourseWriteInput,
  LessonAssetDto,
  LessonAssetKind,
  LessonDetailDto,
  LessonListDto,
  LessonListParams,
  LessonScheduleDto,
  LessonWriteInput,
  ModuleDto,
  ModuleWriteInput,
  RescheduleInput,
  ScheduleFeedParams,
  ScheduleLessonInput,
} from './types';

// ---------------------------------------------------------------------------
// Courses
// ---------------------------------------------------------------------------

/** `GET /api/courses/` — staff see all; pupils get a reduced payload limited
 *  to courses holding lessons visible to them (ADR-011). */
export async function getCourses(params: CourseListParams = {}): Promise<Paginated<CourseDto>> {
  const { data } = await apiClient.get<Paginated<CourseDto>>('/courses/', { params });
  return data;
}

/** `GET /api/courses/{public_id}/`. */
export async function getCourse(publicId: string): Promise<CourseDto> {
  const { data } = await apiClient.get<CourseDto>(`/courses/${publicId}/`);
  return data;
}

/** `POST /api/courses/` — admin & teacher. */
export async function createCourse(input: CourseWriteInput): Promise<CourseDto> {
  const { data } = await apiClient.post<CourseDto>('/courses/', input);
  return data;
}

/** `PATCH /api/courses/{public_id}/`. */
export async function updateCourse(
  publicId: string,
  input: Partial<CourseWriteInput>
): Promise<CourseDto> {
  const { data } = await apiClient.patch<CourseDto>(`/courses/${publicId}/`, input);
  return data;
}

/** `DELETE /api/courses/{public_id}/` — archives (C4). */
export async function archiveCourse(publicId: string): Promise<void> {
  await apiClient.delete(`/courses/${publicId}/`);
}

// ---------------------------------------------------------------------------
// Modules (staff-only surface)
// ---------------------------------------------------------------------------

/** `GET /api/modules/?course=` — ordered within their course. */
export async function getModules(courseId: string): Promise<Paginated<ModuleDto>> {
  const { data } = await apiClient.get<Paginated<ModuleDto>>('/modules/', {
    params: { course: courseId, page_size: 100 },
  });
  return data;
}

/** `POST /api/modules/` — `order` appends server-side. */
export async function createModule(input: ModuleWriteInput): Promise<ModuleDto> {
  const { data } = await apiClient.post<ModuleDto>('/modules/', input);
  return data;
}

/** `PATCH /api/modules/{public_id}/`. */
export async function updateModule(
  publicId: string,
  input: Partial<ModuleWriteInput>
): Promise<ModuleDto> {
  const { data } = await apiClient.patch<ModuleDto>(`/modules/${publicId}/`, input);
  return data;
}

/** `DELETE /api/modules/{public_id}/` — lessons fall back to course level. */
export async function deleteModule(publicId: string): Promise<void> {
  await apiClient.delete(`/modules/${publicId}/`);
}

/** `POST /api/modules/{public_id}/reorder/` — move to a 1-based position. */
export async function reorderModule(publicId: string, order: number): Promise<ModuleDto> {
  const { data } = await apiClient.post<ModuleDto>(`/modules/${publicId}/reorder/`, { order });
  return data;
}

/** `POST /api/lessons/{public_id}/reorder/` — move to a 1-based position
 *  within the lesson's placement group (its module, or course level). */
export async function reorderLesson(publicId: string, order: number): Promise<LessonDetailDto> {
  const { data } = await apiClient.post<LessonDetailDto>(`/lessons/${publicId}/reorder/`, {
    order,
  });
  return data;
}

// ---------------------------------------------------------------------------
// Lessons
// ---------------------------------------------------------------------------

/** `GET /api/lessons/` — staff see all; pupils only published lessons
 *  scheduled to a class they are enrolled in (ADR-011). */
export async function getLessons(params: LessonListParams = {}): Promise<Paginated<LessonListDto>> {
  const { data } = await apiClient.get<Paginated<LessonListDto>>('/lessons/', { params });
  return data;
}

/** `GET /api/lessons/{public_id}/` — blocks arrive read-enriched. */
export async function getLesson(publicId: string): Promise<LessonDetailDto> {
  const { data } = await apiClient.get<LessonDetailDto>(`/lessons/${publicId}/`);
  return data;
}

/** `POST /api/lessons/`. */
export async function createLesson(input: LessonWriteInput): Promise<LessonDetailDto> {
  const { data } = await apiClient.post<LessonDetailDto>('/lessons/', input);
  return data;
}

/** `PATCH /api/lessons/{public_id}/` — save sends the FULL blocks array. */
export async function updateLesson(
  publicId: string,
  input: LessonWriteInput
): Promise<LessonDetailDto> {
  const { data } = await apiClient.patch<LessonDetailDto>(`/lessons/${publicId}/`, input);
  return data;
}

/** `DELETE /api/lessons/{public_id}/` — archives (C4). */
export async function archiveLesson(publicId: string): Promise<void> {
  await apiClient.delete(`/lessons/${publicId}/`);
}

/** `POST /api/lessons/{public_id}/publish/`. */
export async function publishLesson(publicId: string): Promise<LessonDetailDto> {
  const { data } = await apiClient.post<LessonDetailDto>(`/lessons/${publicId}/publish/`);
  return data;
}

/** `POST /api/lessons/{public_id}/unpublish/`. */
export async function unpublishLesson(publicId: string): Promise<LessonDetailDto> {
  const { data } = await apiClient.post<LessonDetailDto>(`/lessons/${publicId}/unpublish/`);
  return data;
}

/** `POST /api/lessons/{public_id}/duplicate/` — returns the new draft copy. */
export async function duplicateLesson(publicId: string): Promise<LessonDetailDto> {
  const { data } = await apiClient.post<LessonDetailDto>(`/lessons/${publicId}/duplicate/`);
  return data;
}

/** `POST /api/lessons/{public_id}/schedule/` — admin or class teacher. */
export async function scheduleLesson(
  publicId: string,
  input: ScheduleLessonInput
): Promise<LessonScheduleDto> {
  const { data } = await apiClient.post<LessonScheduleDto>(`/lessons/${publicId}/schedule/`, input);
  return data;
}

// ---------------------------------------------------------------------------
// Assets (staff-only endpoint — pupils get URLs embedded in lesson payloads)
// ---------------------------------------------------------------------------

/** `POST /api/lesson-assets/` — multipart upload with progress reporting.
 *  Size/type limits are enforced server-side and surface as
 *  `field_errors.file` on the normalized ApiError. */
export async function uploadLessonAsset(
  kind: LessonAssetKind,
  file: File,
  onProgress?: (percent: number) => void
): Promise<LessonAssetDto> {
  const form = new FormData();
  form.append('kind', kind);
  form.append('file', file);
  const { data } = await apiClient.post<LessonAssetDto>('/lesson-assets/', form, {
    onUploadProgress: event => {
      if (onProgress && event.total) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    },
  });
  return data;
}

/** `DELETE /api/lesson-assets/{public_id}/` — admin, or uploader while unreferenced. */
export async function deleteLessonAsset(publicId: string): Promise<void> {
  await apiClient.delete(`/lesson-assets/${publicId}/`);
}

// ---------------------------------------------------------------------------
// Schedule / calendar
// ---------------------------------------------------------------------------

/** `GET /api/schedule/?from=&to=&school_class=` — role-aware calendar feed
 *  (pupils only receive published lessons of their classes). */
export async function getScheduleFeed(
  params: ScheduleFeedParams = {}
): Promise<Paginated<LessonScheduleDto>> {
  const { data } = await apiClient.get<Paginated<LessonScheduleDto>>('/schedule/', { params });
  return data;
}

/** `PATCH /api/lesson-schedules/{public_id}/` — reschedule (time/duration). */
export async function rescheduleLesson(
  publicId: string,
  input: RescheduleInput
): Promise<LessonScheduleDto> {
  const { data } = await apiClient.patch<LessonScheduleDto>(
    `/lesson-schedules/${publicId}/`,
    input
  );
  return data;
}

/** `DELETE /api/lesson-schedules/{public_id}/` — remove from the calendar. */
export async function unscheduleLesson(publicId: string): Promise<void> {
  await apiClient.delete(`/lesson-schedules/${publicId}/`);
}
